import React, { useEffect, useState } from "react";
import {
  Badge,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../redux/reducer/rootReducer";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import ReplayIcon from "@mui/icons-material/Replay";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ReceiptIcon from "@mui/icons-material/Receipt";
import InventoryIcon from "@mui/icons-material/Inventory";
import {
  addDealerDashboardCounts,
  useGetUnreadOrdersAndQuotesCountMutation,
} from "../../redux/reducer/features/Dashboard/DashboardSlice";
import AppConfirmDialog from "../app/AppConfirmDialog";
import { setHasUnsavedChanges } from "../../redux/reducer/features/App/AppGridSlice";
import {
  removeAllDmsParts,
  removeAllDmsWhoolegoods,
} from "../../redux/reducer/features/Integration/DmsSetupSlice";
import { Feature, SetupType } from "../../models/features/Integration/DmsSetup";
import { hasFeatureAccess } from "../../services/featurePermissions/featureMatrix";
import { AccountCircle } from "@mui/icons-material";

const menuItems = [
  { id: 1, text: "Dashboard", icon: DashboardIcon, link: "/" },
  { id: 2, text: "Orders", icon: LibraryBooksIcon, link: "/Orders" },
  { id: 3, text: "Leads", icon: ReceiptIcon, link: "/Leads" },
  {
    id: 4,
    text: "Products",
    icon: ManageSearchIcon,
    link: "/ManageProducts",
  },
  {
    id: 5,
    text: "Sync",
    icon: ReplayIcon,
    link: "/Sync",
  },
  {
    id: 6,
    text: "Used Products",
    icon: InventoryIcon,
    link: "/UsedProducts",
    feature: Feature.UsedProducts,
  },
  {
    id: 7,
    text: "Virtual Showroom",
    icon: NewReleasesIcon,
    link: "/VirtualShowroom",
    feature: Feature.VirtualShowroom,
  },
  {
    id: 8,
    text: "CustomerLink",
    icon: AccountCircle,
    link: "/CustomerLink",
  },
];
export const DealerLeftMenu: React.FunctionComponent<{
  handleCloseSidebar: () => void;
}> = ({ handleCloseSidebar }) => {
  const stats = useSelector(
    (state: AppState) => state.dashboardReducer.dealerDashboard,
  );

  const hasUnsavedChanges = useSelector(
    (state: AppState) => state.AppGridReducer.hasUnsavedChanges,
  );
  const dispatch = useDispatch();
  const [menuList, setMenuList] = useState<any[]>(menuItems);
  const location = useLocation();
  const currentPath = location.pathname;
  const { setupId, dmsSetup } = useSelector(
    (state: AppState) => state.authReducer,
  );
  const [open, setOpen] = useState(false);
  const [redirectedLink, setRedirectedLink] = useState("");
  const previousPathRef = React.useRef("");
  React.useEffect(() => {
    previousPathRef.current = location.pathname;
  }, [location]);

  // Filter menu items based on setupType
  useEffect(() => {
    const filteredMenu = menuItems.filter((item) => {
      if (!item.feature) return true;
      return hasFeatureAccess(dmsSetup?.setupType, item.feature);
    });

    setMenuList(filteredMenu);
  }, [dmsSetup?.setupType]);

  const [unreadStatsMutation, unreadStatsMutationResult] =
    useGetUnreadOrdersAndQuotesCountMutation();
  let navigate = useNavigate();
  useEffect(() => {
    if (setupId) {
      unreadStatsMutation().then((res: any) => {
        if (res && res.data) {
          dispatch(
            addDealerDashboardCounts({
              unreadOrders: res.data.entity.unreadOrders,
              unreadQuotes: res.data.entity.unreadQuotes,
            }),
          );
        }
      });
    }
  }, []);

  const handleRedirect = (link: string, event: React.MouseEvent) => {
    event.preventDefault();
    const isCtrlOrCmdPressed = event.ctrlKey || event.metaKey;
    if (previousPathRef.current === "/Sync" && hasUnsavedChanges) {
      setOpen(true);
      setRedirectedLink(link);
    } else {
      if (isCtrlOrCmdPressed) {
        window.open(link, "_blank");
      } else {
        navigate(link);
      }
    }
  };
  return (
    <>
      {setupId && setupId !== "" ? (
        menuList.map((item, index) => (
          <ListItem
            key={index}
            disablePadding
            sx={{
              display: "block",
              backgroundColor: currentPath === item.link ? "#80808066" : "none",
            }}
          >
            <ListItemButton
              className="left-menu-button"
              onClick={(e) => {
                handleRedirect(item.link, e);
              }}
              component={Link}
              to={item.link}
            >
              <ListItemIcon
                sx={{
                  mr: 3,
                }}
              >
                {item.link === "/Orders" || item.link === "/Leads" ? (
                  <Badge
                    badgeContent={
                      item.link === "/Orders"
                        ? stats?.unreadOrders
                        : stats?.unreadQuotes
                    }
                    color="error"
                    sx={{
                      "& .MuiBadge-standard": {
                        height: "14px", // Set the height for standard badges
                        fontSize: "10px", // Set the font size
                      },
                    }}
                  >
                    <item.icon sx={{ color: "#ffff" }} />
                  </Badge>
                ) : (
                  <item.icon sx={{ color: "#ffff" }} />
                )}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))
      ) : (
        <ListItem key={1} disablePadding sx={{ display: "block" }}>
          <ListItemButton
            className="left-menu-button"
            onClick={(e) => {
              handleRedirect("DmsSetup", e);
            }}
          >
            <ListItemIcon
              sx={{
                mr: 3,
              }}
            >
              <PrecisionManufacturingIcon sx={{ color: "#ffff" }} />
            </ListItemIcon>
            <ListItemText primary={"DMS Setup"} />
          </ListItemButton>
        </ListItem>
      )}
      <AppConfirmDialog
        headerText="Confirm Navigation"
        bodyText="Are you sure you want to leave this page?"
        onFail={() => {
          setOpen(false);
          handleCloseSidebar();
        }}
        onSuccess={() => {
          setOpen(false);
          handleCloseSidebar();
          dispatch(setHasUnsavedChanges(false));
          navigate(redirectedLink);
          dispatch(removeAllDmsWhoolegoods());
          dispatch(removeAllDmsParts());
        }}
        handleClose={() => {
          setOpen(false);
          handleCloseSidebar();
        }}
        isOpen={open}
      />
    </>
  );
};
